import React, { useState, useEffect } from "react";
import { Box, Typography, Alert } from "@mui/material";
import axios from "axios";
import TableWithPagination from "../../../components/TableWithPagination";
import { useLoanContext } from "../LoanContext";
import { useParams } from "react-router-dom";
import { formatDateToDDMMYYYY } from "../../../utils/dateUtils";
import { formatAmount } from "../../../utils/formatters";

interface RepaymentHistory {
  id: string;
  name: string;
  paymentDate: string | Date;
  transactionAmount: number;
  principal: number | null;
  interest: number | null;
  fees: number | null;
}

const formatGBP = (value: unknown): string => {
  if (value === undefined || value === null || value === "") return "£0.00";
  const numValue = Number(value);
  if (Number.isNaN(numValue)) return "£0.00";
  return formatAmount(numValue);
};

const parseDateSafe = (v: unknown): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;

  const str = String(v).trim();
  if (!str) return null;

  // First, try native parsing (covers ISO 8601 and timestamp strings)
  const iso = new Date(str);
  if (!isNaN(iso.getTime())) return iso;

  // Fallback: DD/MM/YYYY
  const ddmmyyyy = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (ddmmyyyy) {
    const [, d, m, y] = ddmmyyyy;
    const year = y.length === 2 ? Number(`20${y}`) : Number(y);
    const parsed = new Date(year, Number(m) - 1, Number(d));
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

const RepaymentHistoryTab: React.FC = () => {
  const { loan } = useLoanContext();
  const loanId = useParams().loanId;

  const [repaymentHistory, setRepaymentHistory] = useState<RepaymentHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loanId || !loan) {
      setError(!loanId ? "No loanId provided." : "Loading loan data...");
      setLoading(false);
      return;
    }
    const fetchRepaymentHistory = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(`/api/repayment-history?loanId=${loanId}`, { withCredentials: true });
        const data = Array.isArray(response.data) ? response.data : [];
        setRepaymentHistory(data);
      } catch (err: unknown) {
        setError(String((err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Failed to fetch repayment history."));
      } finally {
        setLoading(false);
      }
    };
    fetchRepaymentHistory();
  }, [loanId, loan]);

  if (loading)
    return (
      <Box textAlign="center" mt={8}>
        <Typography sx={{ color: "#fff" }}>Loading repayment history...</Typography>
      </Box>
    );
  if (error)
    return (
      <Box textAlign="center" mt={8}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );

  return (
    <Box sx={{ width: "100%", maxWidth: "1280px", mx: "auto", px: 2 }}>
      <Box sx={{ width: "100%", overflowX: "auto", borderRadius: 2 }}>
        <TableWithPagination
          columns={[
            {
              label: "Payment Date",
              key: "paymentDate",
              sortable: true,
              sortValue: (row: Record<string, unknown>) => {
                const parsed = parseDateSafe(row.paymentDate);
                return parsed ? parsed.getTime() : null;
              },
              renderCell: (row: Record<string, unknown>) =>
                row.paymentDate
                  ? formatDateToDDMMYYYY(row.paymentDate as string)
                  : "—",
            },
            {
              label: "Transaction Amount",
              key: "transactionAmount",
              renderCell: (row: Record<string, unknown>) => {
                const value = row.transactionAmount;
                if (value === undefined || value === null || value === "")
                  return "—";
                const numValue = parseFloat(String(value));
                return isNaN(numValue) ? "—" : formatAmount(numValue);
              },
            },
            {
              label: "Principal",
              key: "principal",
              renderCell: (row: Record<string, unknown>) =>
                formatGBP(row.principal),
            },
            {
              label: "Interest",
              key: "interest",
              renderCell: (row: Record<string, unknown>) =>
                formatGBP(row.interest),
            },
            {
              label: "Fees",
              key: "fees",
              renderCell: (row: Record<string, unknown>) =>
                formatGBP(row.fees),
            },
          ]}
          rows={repaymentHistory as unknown as Record<string, unknown>[]}
          getRowKey={(row, index) =>
            `${row.id || "unknown"}-${row.name || "na"}-${row.paymentDate || "na"}-${index ?? 0}`
          }
          rowsPerPage={10}
        />
      </Box>
    </Box>
  );
};

export default RepaymentHistoryTab;

